import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { db, COLLECTIONS } from '../firebase/config';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, where, serverTimestamp } from 'firebase/firestore';
import TabsComponent from '../components/ui/TabsComponent';
import StatusBadge, { PriorityBadge } from '../components/ui/StatusBadge';
// הוסף את ה-import החסר
import SopBuilderWizard from '../components/sop/SopBuilderWizard';

import { 
  FileText, 
  FolderOpen,
  Plus,
  Edit,
  Trash2,
  Copy,
  Calendar,
  Users,
  ChevronRight,
  Package,
  Eye,
  Download,
  Clock,
  CheckSquare,
  Settings,
  Briefcase,
  Target,
  Layers,
  PlayCircle,
  AlertCircle,
  BookOpen,
  TrendingUp,
  X
} from 'lucide-react';

const Templates = () => {
  const { currentUser } = useAuth();
  const { users, departments, createTask, createProject } = useData();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [showApplyWizard, setShowApplyWizard] = useState(false);
  const [applyTarget, setApplyTarget] = useState({ type: 'user', id: currentUser?.id });
  const [previewTasks, setPreviewTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showSopBuilder, setShowSopBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // SOP Template categories
  const categories = [
    { id: 'onboarding', label: 'קליטת עובד', icon: Users, color: 'purple' },
    { id: 'role_training', label: 'הכשרת תפקיד', icon: BookOpen, color: 'blue' },
    { id: 'department_ops', label: 'תהליכי מחלקה', icon: Briefcase, color: 'green' },
    { id: 'management', label: 'ניהול ובקרה', icon: TrendingUp, color: 'orange' }
  ];

  // Subscribe to SOP templates
  useEffect(() => {
    if (!currentUser?.id) return;
    
    // Use orgId if available, otherwise use user ID as fallback
    const orgId = currentUser?.orgId || currentUser?.id || 'default-org';
    
    const q = query(
      collection(db, 'sopTemplates'),
      where('orgId', '==', orgId),
      where('isActive', '==', true)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const templatesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Templates fetched:', templatesList);
      console.log('Query filters - orgId:', orgId, 'isActive: true');
      setTemplates(templatesList);
    });

    return () => unsubscribe();
  }, [currentUser?.id, currentUser?.orgId]);

  // Filter templates by category
  const filteredTemplates = templates.filter(template => 
    filterCategory === 'all' || template.category === filterCategory
  );

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק תבנית זו? פעולה זו לא ניתנת לביטול.')) {
      return;
    }

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'sopTemplates', templateId));
      setShowDeleteConfirm(null);
      alert('התבנית נמחקה בהצלחה');
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('שגיאה במחיקת התבנית');
    } finally {
      setLoading(false);
    }
  };

  // Edit template
  const handleEditTemplate = (template) => {
    setNewTemplate({
      ...template,
      // Make sure we have the required structure
      phases: template.phases || [],
      successMetrics: template.successMetrics || [],
      tags: template.tags || []
    });
    setEditingTemplate(template);
    setShowNewTemplate(true);
  };

  // Update template
  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    
    if (!editingTemplate || !currentUser?.id) {
      alert('שגיאה בעדכון התבנית');
      return;
    }
    
    const orgId = currentUser?.orgId || currentUser?.id;
    
    if (!newTemplate.name || newTemplate.name.trim() === '') {
      alert('נא להזין שם לתבנית');
      return;
    }
    
    setLoading(true);
    try {
      await updateDoc(doc(db, 'sopTemplates', editingTemplate.id), {
        ...newTemplate,
        orgId: orgId,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.id
      });
      
      setShowNewTemplate(false);
      setEditingTemplate(null);
      resetNewTemplate();
      alert('התבנית עודכנה בהצלחה!');
    } catch (error) {
      console.error('Error updating template:', error);
      alert('שגיאה בעדכון התבנית: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Tabs for template gallery
  const templateTabs = [
    {
      id: 'all',
      label: 'כל התבניות',
      icon: FileText,
      badge: templates.length.toString(),
      content: <TemplateGrid 
        templates={filteredTemplates} 
        onSelectTemplate={handleSelectTemplate}
        onEditTemplate={handleEditTemplate}
        onDeleteTemplate={handleDeleteTemplate}
        currentUser={currentUser}
      />
    },
    ...categories.map(category => ({
      id: category.id,
      label: category.label,
      icon: category.icon,
      badge: templates.filter(t => t.category === category.id).length.toString(),
      content: <TemplateGrid 
        templates={templates.filter(t => t.category === category.id)} 
        onSelectTemplate={handleSelectTemplate}
        onEditTemplate={handleEditTemplate}
        onDeleteTemplate={handleDeleteTemplate}
        currentUser={currentUser}
      />
    }))
  ];

  // New SOP template form state
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    category: 'onboarding',
    type: 'sop',
    phases: [{
      id: `ph_${Date.now()}`,
      name: 'שלב 1',
      order: 1,
      goals: [],
      resources: [],
      tasks: []
    }],
    successMetrics: [],
    defaultDurationDays: 30,
    isActive: true,
    version: 1,
    tags: []
  });

  // Task template structure for SOP
  const [taskTemplate, setTaskTemplate] = useState({
    id: '',
    title: '',
    description: '',
    relativeStartDay: 0,
    durationDays: 1,
    assigneeRole: 'target',
    priority: 'medium',
    checklist: [],
    successCriteria: { type: 'checkbox_all' },
    conditions: [],
    resources: []
  });

  // Create SOP template - תיקון 1: וידוא שהפונקציה עובדת כראוי
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    
    // בדיקת תקינות
    if (!currentUser?.id) {
      alert('אין משתמש מחובר');
      return;
    }
    
    const orgId = currentUser?.orgId || currentUser?.id;
    
    if (!newTemplate.name || newTemplate.name.trim() === '') {
      alert('נא להזין שם לתבנית');
      return;
    }
    
    setLoading(true);
    try {
      await addDoc(collection(db, 'sopTemplates'), {
        ...newTemplate,
        orgId: orgId,
        createdBy: currentUser.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      setShowNewTemplate(false);
      resetNewTemplate();
      alert('התבנית נוצרה בהצלחה!');
    } catch (error) {
      console.error('Error creating SOP template:', error);
      alert('שגיאה ביצירת התבנית: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Add phase to template
  const addPhaseToTemplate = () => {
    const newPhase = {
      id: `ph_${Date.now()}`,
      name: `שלב ${newTemplate.phases.length + 1}`,
      order: newTemplate.phases.length + 1,
      goals: [],
      resources: [],
      tasks: []
    };
    
    setNewTemplate({
      ...newTemplate,
      phases: [...newTemplate.phases, newPhase]
    });
  };

  // Add task to phase
  const addTaskToPhase = (phaseId) => {
    if (!taskTemplate.title) return;
    
    const newTask = {
      ...taskTemplate,
      id: `tt_${Date.now()}`
    };
    
    setNewTemplate({
      ...newTemplate,
      phases: newTemplate.phases.map(phase => 
        phase.id === phaseId
          ? { ...phase, tasks: [...phase.tasks, newTask] }
          : phase
      )
    });
    
    // Reset task template
    setTaskTemplate({
      id: '',
      title: '',
      description: '',
      relativeStartDay: 0,
      durationDays: 1,
      assigneeRole: 'target',
      priority: 'medium',
      checklist: [],
      successCriteria: { type: 'checkbox_all' },
      conditions: [],
      resources: []
    });
  };

  // Remove phase from template
  const removePhaseFromTemplate = (phaseId) => {
    setNewTemplate({
      ...newTemplate,
      phases: newTemplate.phases.filter(p => p.id !== phaseId)
    });
  };

  // Remove task from phase
  const removeTaskFromPhase = (phaseId, taskId) => {
    setNewTemplate({
      ...newTemplate,
      phases: newTemplate.phases.map(phase => 
        phase.id === phaseId
          ? { ...phase, tasks: phase.tasks.filter(t => t.id !== taskId) }
          : phase
      )
    });
  };

  // Add success metric
  const addSuccessMetric = (metric) => {
    setNewTemplate({
      ...newTemplate,
      successMetrics: [...newTemplate.successMetrics, metric]
    });
  };

  // Reset form
  const resetNewTemplate = () => {
    setNewTemplate({
      name: '',
      description: '',
      category: 'onboarding',
      type: 'sop',
      phases: [{
        id: `ph_${Date.now()}`,
        name: 'שלב 1',
        order: 1,
        goals: [],
        resources: [],
        tasks: []
      }],
      successMetrics: [],
      defaultDurationDays: 30,
      isActive: true,
      version: 1,
      tags: []
    });
  };


  // Handle template selection
  function handleSelectTemplate(template) {
    setSelectedTemplate(template);
    setPreviewTasks(generatePreviewTasks(template));
  }

  // יצירת משימות מתבנית SOP
  const createTasksFromTemplate = async (instance, template) => {
    let createdTasks = 0;
    
    try {
      // עובר על כל השלבים והמשימות
      for (const phase of template.phases || []) {
        for (const templateTask of phase.tasks || []) {
          // מחשב תאריכים
          const startDate = new Date(instance.startDate);
          const taskStartDate = new Date(startDate);
          taskStartDate.setDate(taskStartDate.getDate() + templateTask.relativeStartDay);
          
          const taskDueDate = new Date(taskStartDate);
          taskDueDate.setDate(taskDueDate.getDate() + templateTask.durationDays);
          
          // קובע מי המבצע
          let assigneeId = instance.targets.userId || instance.appliedBy;
          
          // יוצר משימה
          await createTask({
            title: templateTask.title,
            description: templateTask.description || '',
            assigneeId: assigneeId,
            departmentId: instance.targets.departmentId,
            dueDate: taskDueDate,
            priority: templateTask.priority || 'medium',
            status: 'open',
            // מידע נוסף לקישור ל-SOP
            sopInstanceId: instance.id,
            phaseId: phase.id,
            templateTaskId: templateTask.id
          });
          
          createdTasks++;
        }
      }
      
      // מעדכן את המופע עם מספר המשימות
      await updateDoc(doc(db, 'sopInstances', instance.id), {
        'progress.tasksTotal': createdTasks,
        'progress.tasksDone': 0
      });
      
      return createdTasks;
    } catch (error) {
      console.error('Error creating tasks:', error);
      throw error;
    }
  };

  // Generate preview tasks with dates (now handles phases)
  const generatePreviewTasks = (template, startDate = new Date()) => {
    if (!template.phases) return [];
    
    const allTasks = [];
    
    template.phases.forEach(phase => {
      phase.tasks?.forEach(task => {
        const taskStartDate = new Date(startDate);
        taskStartDate.setDate(taskStartDate.getDate() + task.relativeStartDay);
        
        const taskEndDate = new Date(taskStartDate);
        taskEndDate.setDate(taskEndDate.getDate() + task.durationDays);

        allTasks.push({
          ...task,
          phaseId: phase.id,
          phaseName: phase.name,
          startDate: taskStartDate,
          dueDate: taskEndDate
        });
      });
    });
    
    return allTasks.sort((a, b) => a.startDate - b.startDate);
  };

  // Apply SOP template - creates instance
  const handleApplyTemplate = async () => {
    if (!currentUser?.id || !selectedTemplate) return;
    
    const orgId = currentUser?.orgId || currentUser?.id;
    
    setLoading(true);
    try {
      const startDate = new Date().toISOString();
      const targetUserId = applyTarget.type === 'user' ? applyTarget.id : null;
      const targetDeptId = applyTarget.type === 'department' ? applyTarget.id : currentUser.departmentId;

      // יצירת מופע SOP
      const instanceData = {
        orgId: orgId,
        templateId: selectedTemplate.id,
        appliedAt: serverTimestamp(),
        appliedBy: currentUser.id,
        scope: applyTarget.type,
        targets: {
          userId: targetUserId,
          departmentId: targetDeptId
        },
        startDate: startDate,
        status: 'active',
        progress: {
          tasksTotal: 0,
          tasksDone: 0
        },
        metrics: {}
      };

      // יוצר את המופע ב-Firebase
      const instanceRef = await addDoc(collection(db, 'sopInstances'), instanceData);
      const newInstance = { ...instanceData, id: instanceRef.id };
      
      // יוצר משימות בצד הלקוח
      const tasksCreated = await createTasksFromTemplate(newInstance, selectedTemplate);

      setShowApplyWizard(false);
      setSelectedTemplate(null);
      alert(`תהליך ה-SOP הופעל בהצלחה! נוצרו ${tasksCreated} משימות.`);
    } catch (error) {
      console.error('Error applying SOP template:', error);
      alert('שגיאה בהפעלת התבנית');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">תבניות SOP</h1>
            <p className="text-sm text-gray-600 mt-1">תהליכי עבודה סטנדרטיים לארגון</p>
          </div>
          {currentUser?.role !== 'employee' && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowSopBuilder(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Layers size={20} />
                אשף בניית SOP
              </button>
              <button
                onClick={() => setShowNewTemplate(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus size={20} />
                תבנית חדשה
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Template Gallery with Tabs */}
      <TabsComponent
        tabs={templateTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* New SOP Template Modal */}
      {showNewTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl m-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingTemplate ? 'עריכת תבנית SOP' : 'יצירת תבנית SOP חדשה'}
            </h2>
            <form onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}>
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">שם התבנית</label>
                    <input
                      type="text"
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">קטגוריה</label>
                    <select
                      value={newTemplate.category}
                      onChange={(e) => setNewTemplate({...newTemplate, category: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">תיאור</label>
                  <textarea
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">משך כולל (ימים)</label>
                  <input
                    type="number"
                    value={newTemplate.defaultDurationDays}
                    onChange={(e) => setNewTemplate({...newTemplate, defaultDurationDays: parseInt(e.target.value) || 30})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">תגיות</label>
                  <input
                    type="text"
                    value={newTemplate.tagsText || newTemplate.tags.join(' ')}
                    onChange={(e) => setNewTemplate({
                      ...newTemplate, 
                      tagsText: e.target.value
                    })}
                    onBlur={(e) => setNewTemplate({
                      ...newTemplate,
                      tags: e.target.value.split(/[\s,]+/).map(t => t.trim()).filter(Boolean),
                      tagsText: undefined
                    })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="למשל: מכירות קליטה עובד חדש"
                  />
                  <p className="text-xs text-gray-500 mt-1">כתוב תגיות עם רווחים או פסיקים</p>
                </div>

                {/* Phases Section */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Layers size={20} />
                      שלבי התהליך
                    </h3>
                    <button
                      type="button"
                      onClick={addPhaseToTemplate}
                      className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      הוסף שלב
                    </button>
                  </div>

                  {newTemplate.phases.map((phase, phaseIndex) => (
                    <div key={phase.id} className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <input
                          type="text"
                          value={phase.name}
                          onChange={(e) => {
                            const updatedPhases = [...newTemplate.phases];
                            updatedPhases[phaseIndex].name = e.target.value;
                            setNewTemplate({...newTemplate, phases: updatedPhases});
                          }}
                          className="text-lg font-medium bg-transparent border-b border-gray-300 focus:border-blue-500 outline-none"
                        />
                        {newTemplate.phases.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePhaseFromTemplate(phase.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>

                      {/* Phase Goals - תיקון 2: אפשור רווחים */}
                      <div className="mb-3">
                        <label className="text-sm font-medium">יעדי השלב</label>
                        <textarea
                          placeholder="כתוב יעדים עם רווחים, כל יעד בשורה נפרדת..."
                          value={phase.goalsText || phase.goals.join('\n')}
                          onChange={(e) => {
                            const updatedPhases = [...newTemplate.phases];
                            updatedPhases[phaseIndex].goalsText = e.target.value;
                            setNewTemplate({...newTemplate, phases: updatedPhases});
                          }}
                          onBlur={(e) => {
                            const updatedPhases = [...newTemplate.phases];
                            updatedPhases[phaseIndex].goals = e.target.value
                              .split('\n')
                              .map(g => g.trim())
                              .filter(Boolean);
                            updatedPhases[phaseIndex].goalsText = undefined;
                            setNewTemplate({...newTemplate, phases: updatedPhases});
                          }}
                          className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          rows="3"
                        />
                        <p className="text-xs text-gray-500 mt-1">כתוב בחופשיות - כל יעד בשורה נפרדת</p>
                      </div>

                      {/* Add Task to Phase */}
                      <div className="bg-white p-3 rounded-lg mb-3">
                        <h4 className="text-sm font-medium mb-2">הוסף משימה לשלב</h4>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <input
                            type="text"
                            value={taskTemplate.title}
                            onChange={(e) => setTaskTemplate({...taskTemplate, title: e.target.value})}
                            placeholder="כותרת משימה"
                            className="px-2 py-1 border rounded text-sm"
                          />
                          <select
                            value={taskTemplate.assigneeRole}
                            onChange={(e) => setTaskTemplate({...taskTemplate, assigneeRole: e.target.value})}
                            className="px-2 py-1 border rounded text-sm"
                          >
                            <option value="target">יעד התהליך</option>
                            <option value="manager">מנהל ישיר</option>
                            <option value="it">IT</option>
                            <option value="self">מפעיל התהליך</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <input
                            type="number"
                            value={taskTemplate.relativeStartDay}
                            onChange={(e) => setTaskTemplate({...taskTemplate, relativeStartDay: parseInt(e.target.value) || 0})}
                            placeholder="יום התחלה"
                            className="px-2 py-1 border rounded text-sm"
                            min="0"
                          />
                          <input
                            type="number"
                            value={taskTemplate.durationDays}
                            onChange={(e) => setTaskTemplate({...taskTemplate, durationDays: parseInt(e.target.value) || 1})}
                            placeholder="משך (ימים)"
                            className="px-2 py-1 border rounded text-sm"
                            min="1"
                          />
                          <select
                            value={taskTemplate.priority}
                            onChange={(e) => setTaskTemplate({...taskTemplate, priority: e.target.value})}
                            className="px-2 py-1 border rounded text-sm"
                          >
                            <option value="low">נמוכה</option>
                            <option value="medium">בינונית</option>
                            <option value="high">גבוהה</option>
                            <option value="urgent">דחוף</option>
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={() => addTaskToPhase(phase.id)}
                          disabled={!taskTemplate.title}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          הוסף משימה
                        </button>
                      </div>

                      {/* Phase Tasks List */}
                      <div className="space-y-2">
                        {phase.tasks.map((task, taskIndex) => (
                          <div key={task.id} className="flex items-center justify-between p-2 bg-white border rounded">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">{task.title}</p>
                                <PriorityBadge priority={task.priority} size="xs" />
                              </div>
                              <p className="text-xs text-gray-500">
                                יום {task.relativeStartDay} • {task.durationDays} ימים • {
                                  task.assigneeRole === 'target' ? 'יעד' :
                                  task.assigneeRole === 'manager' ? 'מנהל' :
                                  task.assigneeRole === 'it' ? 'IT' : 'מפעיל'
                                }
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeTaskFromPhase(phase.id, task.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Success Metrics */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Target size={20} />
                    מדדי הצלחה (KPIs)
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newTemplate.successMetrics.some(m => m.kpi === 'onboarding_completion')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addSuccessMetric({ kpi: 'onboarding_completion', target: 0.9, unit: 'ratio' });
                          } else {
                            setNewTemplate({
                              ...newTemplate,
                              successMetrics: newTemplate.successMetrics.filter(m => m.kpi !== 'onboarding_completion')
                            });
                          }
                        }}
                      />
                      <label className="text-sm">אחוז השלמת התהליך (יעד: 90%)</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newTemplate.successMetrics.some(m => m.kpi === 'time_to_productivity')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addSuccessMetric({ kpi: 'time_to_productivity', target: 30, unit: 'days' });
                          } else {
                            setNewTemplate({
                              ...newTemplate,
                              successMetrics: newTemplate.successMetrics.filter(m => m.kpi !== 'time_to_productivity')
                            });
                          }
                        }}
                      />
                      <label className="text-sm">זמן עד פרודוקטיביות מלאה (יעד: 30 ימים)</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewTemplate(false);
                    setEditingTemplate(null);
                    resetNewTemplate();
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  disabled={loading || !newTemplate.name}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      יוצר...
                    </>
                  ) : (
                    editingTemplate ? 'עדכן תבנית' : 'צור תבנית'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Apply Template Wizard */}
      {showApplyWizard && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">הפעל תהליך: {selectedTemplate.name}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">החל על:</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="user"
                      checked={applyTarget.type === 'user'}
                      onChange={(e) => setApplyTarget({type: 'user', id: currentUser.id})}
                    />
                    <span>משתמש ספציפי</span>
                  </label>
                  
                  {currentUser?.role !== 'employee' && (
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="department"
                        checked={applyTarget.type === 'department'}
                        onChange={(e) => setApplyTarget({type: 'department', id: currentUser.departmentId})}
                      />
                      <span>כל המחלקה</span>
                    </label>
                  )}
                </div>
              </div>
 
              {applyTarget.type === 'user' && (
                <div>
                  <label className="block text-sm font-medium mb-1">בחר משתמש</label>
                  <select
                    value={applyTarget.id}
                    onChange={(e) => setApplyTarget({...applyTarget, id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {users.filter(user => user.isActive).map(user => (
                      <option key={user.id} value={user.id}>{user.name} - {user.role}</option>
                    ))}
                  </select>
                </div>
              )}

              {applyTarget.type === 'department' && (
                <div>
                  <label className="block text-sm font-medium mb-1">בחר מחלקה</label>
                  <select
                    value={applyTarget.id}
                    onChange={(e) => setApplyTarget({...applyTarget, id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Preview */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-2">תצוגה מקדימה - משימות שייווצרו:</h3>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {generatePreviewTasks(selectedTemplate).slice(0, 5).map((task, index) => (
                    <div key={index} className="text-sm p-2 bg-gray-50 rounded">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{task.title}</p>
                        <PriorityBadge priority={task.priority} size="xs" />
                      </div>
                      <p className="text-gray-500 text-xs">
                        {task.phaseName} • {task.startDate.toLocaleDateString('he-IL')} - {task.dueDate.toLocaleDateString('he-IL')}
                      </p>
                    </div>
                  ))}
                  {previewTasks.length > 5 && (
                    <p className="text-sm text-gray-500 text-center">
                      ועוד {previewTasks.length - 5} משימות...
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800">
                  <AlertCircle size={16} className="inline mr-1" />
                  תהליך SOP יופעל והמשימות ייווצרו אוטומטית על פי התנאים שהוגדרו.
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowApplyWizard(false);
                  setSelectedTemplate(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                ביטול
              </button>
              <button
                onClick={handleApplyTemplate}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    מפעיל...
                  </>
                ) : (
                  <>
                    <PlayCircle size={18} />
                    הפעל תהליך
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {selectedTemplate && !showApplyWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-xl font-bold">{selectedTemplate.name}</h2>
                  <StatusBadge 
                    status={selectedTemplate.isActive ? 'פעיל' : 'לא פעיל'} 
                    size="sm"
                  />
                </div>
                <p className="text-gray-600">{selectedTemplate.description}</p>
              </div>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">קטגוריה:</span>
                  <span className="mr-2 font-medium">{categories.find(c => c.id === selectedTemplate.category)?.label}</span>
                </div>
                <div>
                  <span className="text-gray-500">משך כולל:</span>
                  <span className="mr-2 font-medium">{selectedTemplate.defaultDurationDays} ימים</span>
                </div>
              </div>

              {/* Phases Display */}
              {selectedTemplate.phases?.map((phase, phaseIndex) => (
                <div key={phase.id} className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold text-lg mb-2">{phase.name}</h3>
                  {phase.goals?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-sm text-gray-600">יעדי השלב:</p>
                      <ul className="list-disc list-inside text-sm text-gray-700">
                        {phase.goals.map((goal, i) => (
                          <li key={i}>{goal}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="space-y-2">
                    {phase.tasks?.map((task, taskIndex) => (
                      <div key={task.id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{task.title}</p>
                              <PriorityBadge priority={task.priority} size="xs" />
                            </div>
                            {task.description && (
                              <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            יום {task.relativeStartDay} • {task.durationDays} ימים
                          </span>
                          <span>•</span>
                          <span>
                            מוקצה ל: {
                              task.assigneeRole === 'self' ? 'מפעיל' : 
                              task.assigneeRole === 'manager' ? 'מנהל' : 
                              task.assigneeRole === 'it' ? 'IT' :
                              'יעד'
                            }
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Success Metrics */}
              {selectedTemplate.successMetrics?.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Target size={18} />
                    מדדי הצלחה
                  </h3>
                  <div className="space-y-1">
                    {selectedTemplate.successMetrics.map((metric, i) => (
                      <div key={i} className="text-sm">
                        <span className="font-medium">{
                          metric.kpi === 'onboarding_completion' ? 'השלמת התהליך' :
                          metric.kpi === 'time_to_productivity' ? 'זמן עד פרודוקטיביות' :
                          metric.kpi
                        }:</span>
                        <span className="mr-2 text-gray-600">
                          יעד {metric.target}{metric.unit === 'ratio' ? '%' : ' ' + metric.unit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowApplyWizard(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <PlayCircle size={18} />
                הפעל תהליך
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SOP Builder Modal */}
      {showSopBuilder && (
        <SopBuilderWizard onClose={() => setShowSopBuilder(false)} />
      )}
    </div>
  );
};

// Template Grid Component
const TemplateGrid = ({ templates, onSelectTemplate, onEditTemplate, onDeleteTemplate, currentUser }) => {
  const categories = [
    { id: 'onboarding', label: 'קליטת עובד', icon: Users, color: 'purple' },
    { id: 'role_training', label: 'הכשרת תפקיד', icon: BookOpen, color: 'blue' },
    { id: 'department_ops', label: 'תהליכי מחלקה', icon: Briefcase, color: 'green' },
    { id: 'management', label: 'ניהול ובקרה', icon: TrendingUp, color: 'orange' }
  ];

  if (templates.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        אין תבניות להצגה
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {templates.map(template => {
        const category = categories.find(c => c.id === template.category);
        const Icon = category?.icon || FileText;
        const totalTasks = template.phases?.reduce((sum, phase) => sum + (phase.tasks?.length || 0), 0) || 0;

        return (
          <div key={template.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-${category?.color || 'gray'}-50 text-${category?.color || 'gray'}-600`}>
                  <Icon size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{template.name}</h3>
                  <p className="text-sm text-gray-500">{category?.label}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onSelectTemplate(template)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  title="תצוגה מקדימה"
                >
                  <Eye size={18} />
                </button>
                <button
                  onClick={() => onSelectTemplate(template)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  title="הפעל תהליך"
                >
                  <PlayCircle size={18} />
                </button>
                {/* Show edit/delete buttons only if user created this template */}
                {(currentUser?.role !== 'employee' && template.createdBy === currentUser?.id) && (
                  <>
                    <button
                      onClick={() => onEditTemplate(template)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                      title="עריכה"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => onDeleteTemplate(template.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="מחיקה"
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
              </div>
            </div>
            
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{template.description}</p>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500 flex items-center gap-1">
                <Layers size={14} />
                {template.phases?.length || 0} שלבים
              </span>
              <span className="text-gray-500 flex items-center gap-1">
                <CheckSquare size={14} />
                {totalTasks} משימות
              </span>
              <span className="text-gray-500 flex items-center gap-1">
                <Calendar size={14} />
                {template.defaultDurationDays} ימים
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Templates;